import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Box, Button, Flex, HStack, VStack, Text, Image } from '@chakra-ui/react'
import { DashboardProfile, EventDetailsResponse, EventStatus } from '../../../services/network'
import { log } from '../../../utils/logger'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { DeleteConfirmationDialog } from '../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog'
import { createEventApi } from '../../../utils/api-interface'
import { formatISODate } from '../../../utils/utils'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/store'
import { getEventStatusLabelKey } from '../../../utils/event-status'

interface IEventInfoProps {
  event: EventDetailsResponse
}

function EventInfo({ event }: IEventInfoProps): JSX.Element {
  const { t } = useTranslation()
  const dashboardUser: DashboardProfile | null = useSelector((state: RootState) => state.dashboardUser.dashboardUser)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const handleCloseDialog = (): void => {
    setIsDialogOpen(false)
  }
  const [isExpanded, setIsExpanded] = useState(false)
  const [dateEvent, setDateEvent] = useState<Date>()
  const today = new Date()

  const toggleExpand = (): void => {
    setIsExpanded(!isExpanded)
  }

  const canDelete = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title === process.env.REACT_APP_ROLE_ADMINISTRATOR
  }, [dashboardUser])

  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (!canDelete) {
      return
    }
    const eventApi = createEventApi()
    try {
      await eventApi.deleteEvent(event.eventId)
    } catch (error) {
      log.error('Error fetching search results:', error)
    }

    setIsDialogOpen(false)
  }, [canDelete, event.eventId])

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'LimitedAvailability':
        return '#DEA006'
      case 'Concluded':
        return '#7C8A97'
      case 'Cancelled':
        return '#551715'
      case 'SoldOut':
        return '#CD1E58'
      default:
        return '#000000'
    }
  }

  const participantsText = useMemo(() => {
    if (event.minParticipants && event.maxParticipants) {
      return t('event_participants_with_min_max', {
        count: event.participantCount,
        min: event.minParticipants,
        max: event.maxParticipants
      })
    }

    if (event.minParticipants) {
      return t('event_participants_with_min', {
        count: event.participantCount,
        min: event.minParticipants
      })
    }

    if (event.maxParticipants) {
      return t('event_participants_with_max', {
        count: event.participantCount,
        max: event.maxParticipants
      })
    }

    return t('event_participants_count', { count: event.participantCount })
  }, [event.maxParticipants, event.minParticipants, event.participantCount, t])

  useEffect(() => {
    eva.replace()
  }, [])

  useEffect(() => {
    const startDate = new Date(event.eventDate)
    setDateEvent(startDate)
  }, [event])

  const navigate = useNavigate()

  const goToCreator = useCallback(() => {
    if (!event) {
      return
    }

    if (!event.organizer) {
      return
    }

    navigate(`/users/userinfo/${event.organizer.userId}`)
  }, [event, navigate])

  return (
    <Flex>
      <Box>
        <Text fontSize='12px' fontWeight='700' lineHeight='14px' textTransform='uppercase' color={'greyCold200'}>
          {t('uploaded_image')}
        </Text>
        <Box position='relative' mt='16px' mb='24px'>
          <Image src={event.eventImageUrl} alt='Event Image' borderRadius='12px' objectFit='cover' width={'390px'} height={'189px'} />
        </Box>

        <VStack rowGap={'24px'}>
          {/* Payment Information */}
          <Flex justifyContent='space-between' alignItems='center' background={'white'} borderRadius={'10px'} height={'62px'} width={'100%'}>
            <Text fontSize='lg' fontWeight='bold' marginLeft={'20px'}>
              {event.isPaid ? t('payment_event') : t('free_event')}
            </Text>
            <Text fontSize='lg' fontWeight='bold' marginRight={'20px'}>
              € {event.payment?.amountSee ? (event.payment.amountSee / 100).toFixed(2) : <>0.00</>}
            </Text>
          </Flex>

          {/* Creator Information */}
          <Flex justifyContent='space-between' alignItems='center' background={'white'} borderRadius={'10px'} height={'62px'} width={'100%'}>
            <Text fontSize='lg' fontWeight='bold' marginLeft={'20px'}>
              {t('organizer_info')}
            </Text>
            <Text marginRight={'20px'} fontWeight='bold' onClick={goToCreator} className='clickableText'>
              @{event.organizer.name.toLowerCase()}
            </Text>
          </Flex>

          {/* Cancel Event */}
          {canDelete && dateEvent && dateEvent > today ? (
            <Flex width={'100%'} justifyContent={'flex-start'}>
              <Button
                variant='outline'
                border='0px'
                leftIcon={<i data-eva={'trash-2'} data-eva-fill={'#353F48'}></i>}
                onClick={(): void => {
                  setIsDialogOpen(true)
                }}>
                {t(`cancel_event`)}
              </Button>
            </Flex>
          ) : (
            <></>
          )}
        </VStack>
      </Box>

      {/* Right Section */}
      <Box width='40%' marginLeft={'72px'}>
        {/* Event Title */}
        <Text fontSize='xl' fontWeight='bold' mb='8px'>
          {event.eventName}
        </Text>

        <Flex
          justifyContent='center'
          alignItems='center'
          background={'greyCold100'}
          borderRadius={'10px'}
          mt='14px'
          mb='14px'
          height={'30px'}
          width={'fit-content'}>
          <Text margin={'10px'} fontSize='12px' fontWeight='700' lineHeight='14px' textTransform='uppercase' color={'greyCold200'}>
            {event.activity.name.it}
          </Text>
        </Flex>
        {/* Event Status Badge */}
        {event.eventStatus !== 'Ok' ? (
          <Badge
            background={getStatusColor(event.eventStatus)}
            borderRadius={'10px'}
            title={
              event.eventStatus === EventStatus.CancelledNoShow
                ? typeof event.successfulCheckInsCount === 'number'
                  ? t('event_check_ins.successful', { value: event.successfulCheckInsCount })
                  : t('event_check_ins.unavailable')
                : undefined
            }>
            <Text margin={'2px'} fontSize='12px' fontWeight='700' lineHeight='14px' textTransform='uppercase' color={'white'}>
              {t(getEventStatusLabelKey(event.eventStatus, event.successfulCheckInsCount))}
            </Text>
          </Badge>
        ) : (
          <></>
        )}

        {/* Event Information */}
        <VStack align='start' spacing='12px' mt='24px'>
          <Text fontSize='16px' fontWeight='700' lineHeight='22.4px' color={'black'}>
            {event.isPrivate ? t('event_info_private') : t('event_info_public')}
          </Text>
          <HStack>
            <Badge alignContent={'center'} background={'#E5EAED'} borderRadius={'6px'} height={'32px'} width={'32px'} key={'clock'}>
              <i data-eva={'clock-outline'} data-eva-fill={'#DD8B25'}></i>
            </Badge>
            <Text fontSize='12px' fontWeight='500' lineHeight='14px' color={'black'}>
              {((Number(event.eventDuration) * 60) / 60).toFixed(0)} h {((Number(event.eventDuration) * 60) % 60).toFixed(0)} m
            </Text>
          </HStack>
          <HStack>
            <Badge alignContent={'center'} height={'32px'} width={'32px'} background={'#E5EAED'} borderRadius={'6px'} key={'clock'}>
              <i data-eva={'people-outline'} data-eva-fill={'#DD8B25'}></i>
            </Badge>
            <VStack alignContent={'flex-start'} justifyContent={'flex-start'} alignItems={'flex-start'}>
              <Text fontSize='12px' fontWeight='500' lineHeight='13px' color={'black'}>
                {participantsText}
              </Text>
              <Text fontSize='12px' fontWeight='500' lineHeight='13px' color={'black'}>
                {event.hasManualParticipantsApproval ? t('manual_approval') : t('automatic_approval')}
              </Text>
            </VStack>
          </HStack>
          {event.description ? (
            <Text fontSize='14px' fontWeight='400' lineHeight='19.6px' color={'black'}>
              <Text noOfLines={isExpanded ? undefined : 4} as='span'>
                {isExpanded ? event.description : event.description.substring(0, 50).concat(event.description.length > 50 ? '...' : '')}
              </Text>
              {event.description.length > 50 ? (
                <Text color='orange.400' as='span' ml='4px' cursor='pointer' onClick={toggleExpand} fontSize='12px' lineHeight='16.8px' fontWeight='600'>
                  {isExpanded ? t('show_less') : t('show_more')}
                </Text>
              ) : (
                <></>
              )}
            </Text>
          ) : (
            <></>
          )}
        </VStack>

        {/* Date and Time */}
        <VStack align='start' spacing='12px' mt='24px'>
          <Text fontSize='16px' fontWeight='700' lineHeight='22.4px' color={'black'}>
            {t('date_time')}
          </Text>
          <HStack>
            <Badge alignContent={'center'} height={'32px'} width={'32px'} background={'#E5EAED'} borderRadius={'6px'} key={'clock'}>
              <i data-eva={'calendar-outline'} data-eva-fill={'#DD8B25'}></i>
            </Badge>
            <Text fontSize='12px' fontWeight='500' lineHeight='14px' color={'black'}>
              {`${t('StartsAt')}: ${formatISODate(event.eventDate)}`}
            </Text>
          </HStack>
          {/* <HStack>
            <Badge alignContent={'center'} height={'32px'} width={'32px'} background={'#E5EAED'} borderRadius={'6px'} key={'clock'}>
              <i data-eva={'calendar-outline'} data-eva-fill={'#DD8B25'}></i>
            </Badge>
            <Text fontSize='12px' fontWeight='500' lineHeight='14px' color={'black'}>
              {`${t('EndsAt')}: ${dayjs(event.eventDate)
                .add(Number(event.eventDuration) * 60, 'minutes')
                .format('DD/MM/YYYY HH:mm')}`}
            </Text>
          </HStack> */}
        </VStack>

        {/* Location */}
        <VStack align='start' spacing='12px' mt='24px'>
          <Text fontSize='16px' fontWeight='700' lineHeight='22.4px' color={'black'}>
            {t('location')}
          </Text>
          <HStack>
            <Badge alignContent={'center'} height={'32px'} width={'32px'} background={'#E5EAED'} borderRadius={'6px'} key={'clock'}>
              <i data-eva={'pin-outline'} data-eva-fill={'#DD8B25'}></i>
            </Badge>
            <Text fontSize='12px' fontWeight='500' lineHeight='14px' color={'black'}>
              {event.locationName} ({event.isOutdoor ? t('EventOutdoor') : t('EventIndoor')})
            </Text>
          </HStack>
          <Text fontSize='12px' fontWeight='500' lineHeight='14px' color='gray.500'>
            {event.address} {event.city}
          </Text>
          <a target='_blank' href={`https://www.google.com/maps/search/?api=1&query=${event.lat},${event.lon}`} rel='noreferrer'>
            <Text color='primary.400' fontSize='14px' fontWeight='600' lineHeight='19.6px'>
              {t('show_map')}
            </Text>
          </a>
        </VStack>
      </Box>
      {canDelete && <DeleteConfirmationDialog isOpen={isDialogOpen} onClose={handleCloseDialog} onConfirm={handleConfirmDelete} type='event' />}
    </Flex>
  )
}

export { EventInfo }
